import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { MrUnitTextPipe } from 'src/app/pipes/mrUnitText.pipe';
import { Column } from 'src/app/models/ui/table/column';

interface ColumnManagerItem {
  id: string;
  label: string;
}

@Component({
  selector: 'app-column-manager-popup',
  templateUrl: './column-manager-popup.component.html',
  styleUrls: ['./column-manager-popup.component.scss'],
  imports: [CommonModule, ClarityModule, DragDropModule, MrTranslatePipe, MrUnitTextPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnManagerPopupComponent implements OnChanges, OnDestroy {
  @Input() public columns: readonly Column[] = [];

  @Input() public buttonLabel = 'Spalten sortieren';
  @Input() public popupTitle = 'Sortieren';

  @Output() public readonly orderChange = new EventEmitter<string[]>();

  protected isOpen = false;
  protected items: ColumnManagerItem[] = [];

  /**
   * Applying a new column order makes Clarity rebuild every `clr-dg-column` and
   * re-layout every rendered `clr-dg-cell`. Running that synchronously inside the
   * CDK drop handler blocks the drop animation, so the item appears to stick to
   * the cursor. The emit is therefore deferred past the drag-release animation
   * and consecutive drags are coalesced into a single grid rebuild.
   */
  private static readonly REORDER_EMIT_DELAY_MS = 250;

  private emitHandle: ReturnType<typeof setTimeout> | null = null;
  private lastEmittedOrder: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.syncItemsWithInputs();
    }
  }

  ngOnDestroy(): void {
    this.flushPendingEmit();
  }

  protected openPopup(): void {
    this.syncItemsWithInputs();
    this.isOpen = true;
  }

  protected closePopup(): void {
    this.setOpen(false);
  }

  /**
   * Bound to (clrModalOpenChange) so closing via the X button or ESC goes through the
   * same path as the footer button and cannot swallow a queued reorder.
   */
  protected setOpen(open: boolean): void {
    if (!open) this.flushPendingEmit();
    this.isOpen = open;
  }

  protected moveItem(event: CdkDragDrop<ColumnManagerItem[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);
    this.scheduleEmit();
  }

  private syncItemsWithInputs(): void {
    // While a reorder is queued our local list is newer than the inputs the parent
    // still holds. Rebuilding from them would visibly revert the drag.
    if (this.emitHandle) return;

    const next = this.columns.map(column => {
      const id = this.normalizeId(column.id);
      return {
        id,
        label: column.bezeichnung || column.id,
      };
    });

    // `next` mirrors what the parent currently renders, so it is the baseline a
    // later drag has to differ from to be worth a rebuild.
    this.lastEmittedOrder = next.map(item => item.id).join('\u0000');

    // The parent pushes a fresh `columns` array in response to our own orderChange
    // emit. Rebuilding `items` then throws away the cdkDrag elements
    // right after a drop, which is both visually jarring and expensive.
    if (this.sameItems(next, this.items)) return;

    this.items = next;
  }

  private sameItems(a: readonly ColumnManagerItem[], b: readonly ColumnManagerItem[]): boolean {
    return (
      a.length === b.length &&
      a.every((item, index) => item.id === b[index].id && item.label === b[index].label)
    );
  }

  private scheduleEmit(): void {
    if (this.emitHandle) clearTimeout(this.emitHandle);

    // Dragging an item away and back again ends up at the order the parent already
    // has. Emitting it would cost a full datagrid rebuild for no visible change.
    if (this.currentOrderKey() === this.lastEmittedOrder) {
      this.emitHandle = null;
      return;
    }

    this.emitHandle = setTimeout(
      () => this.emitOrder(),
      ColumnManagerPopupComponent.REORDER_EMIT_DELAY_MS
    );
  }

  private flushPendingEmit(): void {
    if (!this.emitHandle) return;
    this.emitOrder();
  }

  private emitOrder(): void {
    if (this.emitHandle) {
      clearTimeout(this.emitHandle);
      this.emitHandle = null;
    }

    const order = this.items.map(item => item.id);

    this.lastEmittedOrder = order.join('\u0000');
    this.orderChange.emit(order);
  }

  private currentOrderKey(): string {
    return this.items.map(item => item.id).join('\u0000');
  }

  private normalizeId(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }
}
